"use client";

import React, { useState } from "react";
import { 
  Palette, 
  FileText, 
  Video, 
  Brain, 
  Code, 
  Languages, 
  TrendingUp,
  Target,
  Mail,
  BarChart3,
  Globe,
  Zap,
  ArrowRight,
  Star,
  Shield,
  Clock
} from "lucide-react";

interface FeatureShowcaseProps {
  className?: string;
}

export default function FeatureShowcase({ className = "" }: FeatureShowcaseProps) {
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    { id: "all", name: "Todas", icon: Star },
    { id: "creation", name: "Criação", icon: Palette },
    { id: "marketing", name: "Marketing", icon: Target },
    { id: "analysis", name: "Análise", icon: Brain },
    { id: "technical", name: "Técnico", icon: Code }
  ];

  const allFeatures = [
    {
      id: "image",
      category: "creation",
      icon: Palette,
      title: "Geração de Imagens",
      description: "Crie imagens impressionantes com IA a partir de descrições textuais em diversos estilos.",
      color: "from-blue-500 to-cyan-500",
      gradient: "bg-gradient-to-br from-blue-50 to-cyan-50",
      borderColor: "border-blue-200",
      features: ["Estilos variados", "Alta resolução", "Descrições detalhadas"]
    },
    {
      id: "pitch",
      category: "creation",
      icon: FileText,
      title: "Gerador de Pitch",
      description: "Desenvolva pitches de vendas persuasivos e profissionais para qualquer produto.",
      color: "from-green-500 to-emerald-500",
      gradient: "bg-gradient-to-br from-green-50 to-emerald-50",
      borderColor: "border-green-200",
      features: ["Persuasão", "Profissionalismo", "Adaptável"]
    },
    {
      id: "video",
      category: "creation",
      icon: Video,
      title: "Script de Vídeo",
      description: "Gere scripts completos para vídeos com hooks, estrutura e chamadas para ação.",
      color: "from-purple-500 to-pink-500",
      gradient: "bg-gradient-to-br from-purple-50 to-pink-50",
      borderColor: "border-purple-200",
      features: ["Estrutura completa", "Hooks eficazes", "CTA poderoso"]
    },
    {
      id: "viral",
      category: "marketing",
      icon: TrendingUp,
      title: "Conteúdo Viral",
      description: "Crie conteúdo com potencial de viralização para redes sociais.",
      color: "from-orange-500 to-red-500",
      gradient: "bg-gradient-to-br from-orange-50 to-red-50",
      borderColor: "border-orange-200",
      features: ["Tendências", "Engajamento", "Alcance viral"]
    },
    {
      id: "black-friday",
      category: "marketing",
      icon: Target,
      title: "Black Friday",
      description: "Ofertas irresistíveis e campanhas para a Black Friday.",
      color: "from-red-600 to-orange-600",
      gradient: "bg-gradient-to-br from-red-50 to-orange-50",
      borderColor: "border-red-200",
      features: ["Urgência", "Descontos", "Máximo conversão"]
    },
    {
      id: "email",
      category: "marketing",
      icon: Mail,
      title: "Email Sequence",
      description: "Sequências de email automatizadas para nutrição de leads.",
      color: "from-blue-600 to-indigo-600",
      gradient: "bg-gradient-to-br from-blue-50 to-indigo-50",
      borderColor: "border-blue-200",
      features: ["Automação", "Personalização", "Taxa de abertura"]
    },
    {
      id: "funnel",
      category: "marketing",
      icon: BarChart3,
      title: "Funil de Vendas",
      description: "Otimize seu processo de vendas com funis eficientes.",
      color: "from-green-600 to-teal-600",
      gradient: "bg-gradient-to-br from-green-50 to-teal-50",
      borderColor: "border-green-200",
      features: ["Otimização", "Conversão", "Métricas"]
    },
    {
      id: "landing",
      category: "marketing",
      icon: Globe,
      title: "Landing Page",
      description: "Páginas de alta conversão para capturar leads.",
      color: "from-purple-600 to-pink-600",
      gradient: "bg-gradient-to-br from-purple-50 to-pink-50",
      borderColor: "border-purple-200",
      features: ["Alta conversão", "Design responsivo", "Otimização"]
    },
    {
      id: "sentiment",
      category: "analysis",
      icon: Brain,
      title: "Análise de Sentimento",
      description: "Analise textos para detectar emoções e insights detalhados.",
      color: "from-red-500 to-orange-500",
      gradient: "bg-gradient-to-br from-red-50 to-orange-50",
      borderColor: "border-red-200",
      features: ["Detecção emocional", "Insights", "Precisão"]
    },
    {
      id: "osint",
      category: "analysis",
      icon: Shield,
      title: "OSINT",
      description: "Análise de inteligência de fontes abertas.",
      color: "from-indigo-500 to-purple-500",
      gradient: "bg-gradient-to-br from-indigo-50 to-purple-50",
      borderColor: "border-indigo-200",
      features: ["Fontes abertas", "Segurança", "Investigação"]
    },
    {
      id: "analytics",
      category: "analysis",
      icon: BarChart3,
      title: "Análise de Vendas",
      description: "Dashboard completo com métricas e insights de vendas.",
      color: "from-teal-500 to-green-500",
      gradient: "bg-gradient-to-br from-teal-50 to-green-50",
      borderColor: "border-teal-200",
      features: ["Dashboard", "Métricas", "Insights"]
    },
    {
      id: "code",
      category: "technical",
      icon: Code,
      title: "Gerador de Código",
      description: "Gere código de alta qualidade em múltiplas linguagens.",
      color: "from-yellow-500 to-amber-500",
      gradient: "bg-gradient-to-br from-yellow-50 to-amber-50",
      borderColor: "border-yellow-200",
      features: ["Múltiplas linguagens", "Qualidade", "Documentação"]
    },
    {
      id: "translate",
      category: "technical",
      icon: Languages,
      title: "Tradutor Avançado",
      description: "Traduza textos com contexto cultural e expressões idiomáticas.",
      color: "from-indigo-500 to-blue-500",
      gradient: "bg-gradient-to-br from-indigo-50 to-blue-50",
      borderColor: "border-indigo-200",
      features: ["Contexto cultural", "Idiomatismo", "Precisão"]
    },
    {
      id: "summarize",
      category: "technical",
      icon: FileText,
      title: "Resumidor Inteligente",
      description: "Crie resumos detalhados com pontos principais.",
      color: "from-pink-500 to-rose-500",
      gradient: "bg-gradient-to-br from-pink-50 to-rose-50",
      borderColor: "border-pink-200",
      features: ["Pontos principais", "Detalhes", "Eficiência"]
    }
  ];

  const filteredFeatures = activeCategory === "all" 
    ? allFeatures 
    : allFeatures.filter(feature => feature.category === activeCategory);

  return (
    <div className={`py-16 sm:py-20 bg-gray-50 ${className}`}>
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Todas as Funcionalidades
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            Explore nossa suite completa de ferramentas de inteligência artificial
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeCategory === category.id
                  ? "bg-purple-600 text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-purple-50 border border-gray-200"
              }`}
            >
              <category.icon className="w-4 h-4 mr-2" />
              {category.name}
            </button>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredFeatures.map((feature) => (
            <div
              key={feature.id}
              className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border ${feature.borderColor}`}
            >
              {/* Header */}
              <div className={`bg-gradient-to-br ${feature.gradient} p-6 sm:p-8 relative`}>
                <div className={`bg-gradient-to-r ${feature.color} w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                  <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                  {feature.description}
                </p>
              </div>
              
              {/* Features List */}
              <div className="p-4 sm:p-6">
                <div className="space-y-2 mb-4">
                  {feature.features.map((item, index) => (
                    <div key={index} className="flex items-center text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                      {item}
                    </div>
                  ))}
                </div>
                
                <button className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 group">
                  <span>Experimentar</span>
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-16 sm:mt-20 bg-white rounded-2xl shadow-lg p-8 sm:p-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Por que escolher a Axion IA?
            </h3>
            <p className="text-gray-600 text-lg">
              A combinação perfeita de poder, simplicidade e resultados
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Ultra Rápido</h4>
              <p className="text-gray-600">
                Resultados em segundos, não minutos
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">100% Seguro</h4>
              <p className="text-gray-600">
                Seus dados estão sempre protegidos
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">24/7 Online</h4>
              <p className="text-gray-600">
                Disponível quando você precisar
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
